import type {
  EntryTypes,
  HumanizerResult,
  ProvenanceV2EntryBase,
  ProvenanceV2EntryBaseCommon,
  ProvenanceV2EntryBaseResultsWrapper,
  TextAnalyzeModelName,
  TextAnalyzeResultMap,
  UnknownProvenanceModel,
} from "./types/provenance";

export type TextAnalyzeResults<TModelNames extends TextAnalyzeModelName> = {
  [Model in TModelNames]: TextAnalyzeResultMap[Model];
};

export type HumanizeResponse = {
  id: string;
  content: string;
  results: { HUMANIZER: HumanizerResult };
};
export type HumanizeOptions = {
  style: "aggressive" | "balanced" | "light";
};
export async function humanizeText({
  text,
  options,
}: {
  text: string;
  options: HumanizeOptions & ProvenanceExtraOptions;
}): Promise<
  ProvenanceV2EntryBase<
    "HUMANIZER",
    {
      task_id: string;
      text: string;
      parent_id: null | string;
      entry_type: "CONTENT_HUMANIZER";
      request_source: "website";
    }
  >
> {
  (void text, options);
  return Promise.resolve(
    createProvenanceV2Entry<
      "HUMANIZER",
      {
        task_id: string;
        text: string;
        parent_id: null | string;
        entry_type: "CONTENT_HUMANIZER";
        request_source: "website";
      }
    >(
      {
        model: 7,
        model_name: "HUMANIZER",
        result: {
          text: 'At Polygraf AI we are running Small Language Models (SLMs) on a customers own infrastructure/cloud environment (no cloud data risk) in real time to detect threats – fraud, deepfakes, data loss – and it means we think compliance & privacy by design. As in our TechCrunch announcement says Polygraf offers "award winning AI-native data integrity, monitoring & governance solutions on on-prem Zero Trust framework" In a nutshell we want AI to work along side humans without safety/privacy at stake. Our Mission & our culture are there to show it. We talk about "bring clarity to chaos" turning into understandable and accountable insights the AI content overload. We advocate for Human values : AI should be there to work along side our human abilities without safety/privacy at stake. We say we want our customers to trust AI giving them the tools to verify and control it. Why SLM & on-prem ? The LLMs we see today are big guns, but many companies (15% of enterprise in the world) put their AI on-prem for security/sovereignty reasons. That is why Polygraf is being build on standard server (>=8GB RAM, 1.3GHz CPU) not in the cloud and it is also on-prem: no data spillover, customer data never leaving their environment & not being used for training any models: We guarantee full audit trails & explainability to have full transparency about every detection & redaction we make.',
          source_detection: {
            OPEN_AI: 0,
            MISTRAL: 0,
            CLAUDE: 0,
            GEMINI: 0,
            GRAMMAR_ENHANCED: 0.0005293466426707916,
            HUMAN: 0.9994706533573292,
          },
        },
      },
      {
        task_id: crypto.randomUUID(),
        text: provenanceSampleText,
        parent_id: null,
        entry_type: "CONTENT_HUMANIZER",
        request_source: "website",
      },
    ),
  );
}

const tokenizerPromise = import("@huggingface/transformers").then(
  ({ BertTokenizer }) =>
    BertTokenizer.from_pretrained("google-bert/bert-base-uncased"),
);

type Tokenized = {
  input_ids: { size: number };
};

export async function countTokens(text: string): Promise<number> {
  if (text === "") return 0; // do not show 2 tokens on empty input
  const tokenizer = await tokenizerPromise;
  const { input_ids } = tokenizer(text) as Tokenized;
  return input_ids.size;
}

type ProvenanceExtraOptions = Partial<{
  parent_id: string;
  entry_type: EntryTypes;
  request_source: "website" | ({} & string);
}>;

function createProvenanceV2Entry<
  TModel extends
    | TextAnalyzeModelName
    | UnknownProvenanceModel = UnknownProvenanceModel,
  TPayload = unknown,
>(
  data: ProvenanceV2EntryBaseResultsWrapper<TModel>,
  payload: TPayload,
): ProvenanceV2EntryBase<TModel, TPayload> {
  const base = {
    history_id: crypto.randomUUID(),
    payload: {
      /** JSON string of the payload */
      text_inputs: payload,
      // TODO: add file input type
      file_inputs: [],
      query_params: {},
    },
    source: "plan",
    tokens_used: 1234,
    parent_id: null,
    entry_type: "DATA_PROVENANCE",
    request_source: "website",
    created_at: "2025-10-02T08:26:57.349890Z",
  } as const satisfies ProvenanceV2EntryBaseCommon<TPayload>;

  return {
    ...base,
    ...data,
  };
}

export const provenanceSampleText = `At Polygraf AI, we run Small Language Models (SLMs) on the customer’s own hardware or cloud environment (no cloud data risk) to detect threats – fraud, deepfakes, data loss – in real time. It also means we design everything with compliance and privacy in mind. As our TechCrunch announcement puts it, Polygraf provides “award-winning AI-native data integrity, monitoring & governance solutions via an on-prem, Zero Trust framework”. In short, we want AI to augment human work without compromising safety or privacy.
Our mission and culture reflect that vision. We talk about bringing “clarity within chaos” – by turning the surge of AI content into understandable, accountable insights. We emphasize human values: AI should augment human capabilities, without compromising safety, privacy, or ethical standards. I like to say our goal is to make our customers trust AI , by giving them the tools to verify and control it.
Why SLM and on-prem? Today’s big LLMs are powerful, but many organizations (about 15% of enterprises worldwide) choose on-prem AI for security and sovereignty. That’s why Polygraf is built to run on a standard server (≥8GB RAM, 1.3GHz CPU), not in the cloud. This on-prem architecture eliminates data spillage: customer data never leaves their environment or is used to train any models. We ensure full audit trails and explainability, so every detection or redaction we perform is fully transparent.`;

export function aiDetection(
  text: string,
  options?: ProvenanceExtraOptions,
): Promise<ProvenanceV2EntryBase<"AI_DETECTION", string>> {
  (void text, options);
  return Promise.resolve(
    createProvenanceV2Entry<"AI_DETECTION", string>(
      {
        model: 1,
        model_name: "AI_DETECTION",
        result: {
          AI: 0.5,
          HUMAN: 0.5,
          cognitive_flow_index: 0.5,
        },
      },
      JSON.stringify({
        task_id: crypto.randomUUID(),
        text: provenanceSampleText,
        parent_id: null,
        entry_type: "DATA_PROVENANCE",
        request_source: "website",
      }),
    ),
  );
}
export function sourceDetection(
  text: string,
  options?: ProvenanceExtraOptions,
): Promise<ProvenanceV2EntryBase<"SOURCE_DETECTION", string>> {
  (void text, options);
  return Promise.resolve(
    createProvenanceV2Entry<"SOURCE_DETECTION", string>(
      {
        model: 2,
        model_name: "SOURCE_DETECTION",
        result: {
          OPEN_AI: 0,
          MISTRAL: 0,
          CLAUDE: 1.411256833595389e-8,
          GEMINI: 0.23676960728705898,
          GRAMMAR_ENHANCED: 0,
          HUMAN: 0.7632303786003727,
          cognitive_flow_index: 48.08,
        },
      },
      JSON.stringify({
        task_id: crypto.randomUUID(),
        text: provenanceSampleText,
        parent_id: null,
        entry_type: "DATA_PROVENANCE",
        request_source: "website",
      }),
    ),
  );
}
export function writingAnalysis(
  text: string,
  options?: ProvenanceExtraOptions,
): Promise<ProvenanceV2EntryBase<"WRITING_ANALYSIS", string>> {
  (void text, options);
  return Promise.resolve(
    createProvenanceV2Entry<"WRITING_ANALYSIS", string>(
      {
        model: 3,
        model_name: "WRITING_ANALYSIS",
        result: {
          readability: 61.435681965093735,
          syntactic_tree_depth: 49.23076923076923,
          vocabulary_richness: 80.13698630136986,
          perplexity: 100,
          average_sentence_length: 52.88461497781066,
          average_word_length: 80.43478255040958,
          cognitive_flow_index: 71.83,
        },
      },
      JSON.stringify({
        task_id: crypto.randomUUID(),
        text: provenanceSampleText,
        parent_id: null,
        entry_type: "DATA_PROVENANCE",
        request_source: "website",
      }),
    ),
  );
}
export function quillbotAnalysis(
  text: string,
  options?: ProvenanceExtraOptions,
): Promise<ProvenanceV2EntryBase<"QUILLBOT_ANALYSIS", string>> {
  (void text, options);
  return Promise.resolve(
    createProvenanceV2Entry<"QUILLBOT_ANALYSIS", string>(
      {
        model: 4,
        model_name: "QUILLBOT_ANALYSIS",
        result: {
          Altered: 0.002685314569717798,
          Original: 0.9973146854302822,
        },
      },
      JSON.stringify({
        task_id: crypto.randomUUID(),
        text: provenanceSampleText,
        parent_id: null,
        entry_type: "DATA_PROVENANCE",
        request_source: "website",
      }),
    ),
  );
}
export function plagiarismDetection(
  text: string,
  options?: ProvenanceExtraOptions,
): Promise<ProvenanceV2EntryBase<"PLAGIARISM", string>> {
  (void text, options);
  return Promise.resolve(
    createProvenanceV2Entry<"PLAGIARISM", string>(
      {
        model: 6,
        model_name: "PLAGIARISM",
        result: {
          chunks: [
            {
              chunk:
                " At Polygraf AI, we run Small Language Models (SLMs) on the customer\u2019s own hardware or cloud environment (no cloud data risk) to detect threats \u2013 fraud, deepfakes, data loss \u2013 in real time. It also means we design everything with compliance and privacy in mind. As our TechCrunch announcement puts it, Polygraf provides \u201caward-winning AI-native data integrity, monitoring & governance solutions via an on-prem, Zero Trust framework\u201d. In short, we want AI to augment human work without compromising safety or privacy. Our mission and culture reflect that vision.",
              idx: null,
              color: null,
              score: null,
            },
            {
              chunk:
                " We talk about bringing \u201cclarity within chaos\u201d \u2013 by turning the surge of AI content into understandable, accountable insights.",
              idx: 2,
              color: "#e1ed72",
              score: 27.74,
            },
            {
              chunk:
                " We emphasize human values: AI should augment human capabilities, without compromising safety, privacy, or ethical standards.",
              idx: 1,
              color: "#d6b929",
              score: 38.89,
            },
            {
              chunk:
                " I like to say our goal is to make our customers trust AI , by giving them the tools to verify and control it. Why SLM and on-prem? Today\u2019s big LLMs are powerful, but many organizations (about 15% of enterprises worldwide) choose on-prem AI for security and sovereignty. That\u2019s why Polygraf is built to run on a standard server (\u22658GB RAM, 1.3GHz CPU), not in the cloud. This on-prem architecture eliminates data spillage: customer data never leaves their environment or is used to train any models. We ensure full audit trails and explainability, so every detection or redaction we perform is fully transparent.",
              idx: null,
              color: null,
              score: null,
            },
          ],
          category_averages: {
            "Internet Source": 27.74,
            "Government Official": 38.89,
            "Overall Similarity": 5.13,
            "Student Publications": 0,
            Wikihost: 0,
            "Official News": 0,
            "Online Learning": 0,
            Publications: 0,
          },
          sources: [
            {
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10879008/",
              url_category: "Government Official",
              score: 38.89,
              idx: 1,
              matched_content:
                "Instead, they are poised to augment the capabilities of human caregivers. ... safety, privacy, and compliance with healthcare standards\u00a0...",
              color: "#d6b929",
            },
            {
              url: "https://issuu.com/addictlab/docs/sdgzine05",
              url_category: "Internet Source",
              score: 27.74,
              idx: 2,
              matched_content:
                "Together, we're turning ideas into impact, and we ... It's about finding clarity within chaos to create something that can truly progress humanity\u00a0...",
              color: "#e1ed72",
            },
          ],
        },
      },
      JSON.stringify({
        task_id: crypto.randomUUID(),
        text: provenanceSampleText,
        parent_id: null,
        entry_type: "DATA_PROVENANCE",
        request_source: "website",
      }),
    ),
  );
}
export function aiHighlighter(
  text: string,
  options?: ProvenanceExtraOptions,
): Promise<ProvenanceV2EntryBase<"AI_DETECTION_HIGHLIGHTER", string>> {
  (void text, options);
  return Promise.resolve(
    createProvenanceV2Entry<"AI_DETECTION_HIGHLIGHTER", string>(
      {
        model: 5,
        model_name: "AI_DETECTION_HIGHLIGHTER",
        result: {
          overall_score: {
            OPEN_AI: 0,
            MISTRAL: 0,
            CLAUDE: 0,
            GEMINI: 0,
            GRAMMAR_ENHANCED: 0.6303525093274238,
            HUMAN: 0.3696474906725762,
            cognitive_flow_index: 71.83,
          },
          sentence_data: [
            {
              sentence:
                "At Polygraf AI, we run Small Language Models (SLMs) on the customer’s own hardware or cloud environment (no cloud data risk) to detect threats – fraud, deepfakes, data loss – in real time.",
              color: "#ffffff",
              paragraph_idx: 0,
              score: 3.4820556640625,
              label: "HUMAN",
            },
            {
              sentence:
                "It also means we design everything with compliance and privacy in mind.",
              color: "#ffffff",
              paragraph_idx: 0,
              score: 25.61798095703125,
              label: "HUMAN",
            },
            {
              sentence:
                "As our TechCrunch announcement puts it, Polygraf provides “award-winning AI-native data integrity, monitoring & governance solutions via an on-prem, Zero Trust framework”.",
              color: "#ffffff",
              paragraph_idx: 0,
              score: 46.83125813802083,
              label: "HUMAN",
            },
            {
              sentence:
                "In short, we want AI to augment human work without compromising safety or privacy.",
              color: "#fa0000",
              paragraph_idx: 0,
              score: 75.40690104166666,
              label: "AI",
            },
            {
              sentence: "Our mission and culture reflect that vision.",
              color: "#fa0000",
              paragraph_idx: 1,
              score: 92.7734375,
              label: "AI",
            },
            {
              sentence:
                "We talk about bringing “clarity within chaos” – by turning the surge of AI content into understandable, accountable insights.",
              color: "#fa0000",
              paragraph_idx: 1,
              score: 91.32486979166666,
              label: "AI",
            },
            {
              sentence:
                "We emphasize human values: AI should augment human capabilities, without compromising safety, privacy, or ethical standards.",
              color: "#fa0000",
              paragraph_idx: 1,
              score: 87.28841145833334,
              label: "AI",
            },
            {
              sentence:
                "I like to say our goal is to make our customers trust AI , by giving them the tools to verify and control it.",
              color: "#ffffff",
              paragraph_idx: 1,
              score: 54.06519571940104,
              label: "HUMAN",
            },
            {
              sentence: "Why SLM and on-prem?",
              color: "#ffffff",
              paragraph_idx: 2,
              score: 35.054779052734375,
              label: "HUMAN",
            },
            {
              sentence:
                "Today’s big LLMs are powerful, but many organizations (about 15% of enterprises worldwide) choose on-prem AI for security and sovereignty.",
              color: "#ffffff",
              paragraph_idx: 2,
              score: 39.140065511067704,
              label: "HUMAN",
            },
            {
              sentence:
                "That’s why Polygraf is built to run on a standard server (≥8GB RAM, 1.3GHz CPU), not in the cloud.",
              color: "#fa0000",
              paragraph_idx: 2,
              score: 72.34700520833334,
              label: "AI",
            },
            {
              sentence:
                "This on-prem architecture eliminates data spillage: customer data never leaves their environment or is used to train any models.",
              color: "#fa0000",
              paragraph_idx: 2,
              score: 96.337890625,
              label: "AI",
            },
            {
              sentence:
                "We ensure full audit trails and explainability, so every detection or redaction we perform is fully transparent.",
              color: "#fa0000",
              paragraph_idx: 2,
              score: 99.78841145833334,
              label: "AI",
            },
          ],
        },
      },
      JSON.stringify({
        task_id: crypto.randomUUID(),
        text: provenanceSampleText,
        parent_id: null,
        entry_type: "DATA_PROVENANCE",
        request_source: "website",
      }),
    ),
  );
}
